import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Autocomplete,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { PhotoCamera, PhotoLibrary, Delete } from "@mui/icons-material";
import { handleError, fetchForInput, removeNullsAndEmptys, validateNonEmpty } from "../../utils/lib";
import { uploadPhotoAndThumbnail } from "../../utils/fileUploadHelper";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import EditPage from "./EditPage";
import { v4 as uuidv4 } from 'uuid';
import InlinePhotoCapture from "../../components/InlinePhotoCapture";
import { useTranslation } from 'react-i18next';

const EditProduct = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const {updateProduct} = location.state || {};

  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({ original: null, avatar: null });
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const fileInputRef = useRef(null);

  // Map enum value to display string
  const mapEnumToPriceFormat = (enumFormat) => {
    if (!enumFormat) return null;
    
    if (enumFormat === "FIXED") return t('common.fixed');
    if (enumFormat === "COST_MARGIN") return t('common.costMargin');
    if (enumFormat === "COST_MARGIN_TAX") return t('common.costMarginTax');
    
    return enumFormat;
  };

  const [product, setProduct] = useState(
    updateProduct ? {
      ...updateProduct,
      // Ensure these properties are never undefined and convert enum to user-friendly strings
      format_p1: mapEnumToPriceFormat(updateProduct.format_p1 ?? null),
      format_p2: mapEnumToPriceFormat(updateProduct.format_p2 ?? null),
      format_p3: mapEnumToPriceFormat(updateProduct.format_p3 ?? null),
    } : {
      name: "",
      categories: [],
      unit: null,
      bar_code: "",
      qr_code: "",
      web_page: "",
      quantity: "",
      min_quantity: "",
      cost: "",
      tax: "",
      expires: 0,
      // Price 1
      format_p1: null,
      margin_p1: "",
      fixed_p1: "",
      // Price 2
      format_p2: null,
      margin_p2: "",
      fixed_p2: "",
      // Price 3
      format_p3: null,
      margin_p3: "",
      fixed_p3: "",
    }
  );

  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  
  const [errors, setErrors] = useState({});
  const [refs, setRefs] = useState({
    name: useRef(null),
    categories: useRef(null),
    unit: useRef(null),
    bar_code: useRef(null),
    qr_code: useRef(null),
    web_page: useRef(null),
    quantity: useRef(null),
    min_quantity: useRef(null),
    cost: useRef(null),
    tax: useRef(null),
    // Price 1 refs
    format_p1: useRef(null),
    margin_p1: useRef(null),
    fixed_p1: useRef(null),
    // Price 2 refs
    format_p2: useRef(null),
    margin_p2: useRef(null),
    fixed_p2: useRef(null),
    // Price 3 refs
    format_p3: useRef(null),
    margin_p3: useRef(null),
    fixed_p3: useRef(null),
  });

  const [checked, setChecked] = useState(product.expires === 1);

  useEffect(() => {
    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || product.thumb_url || '') : '';
    
    updateTitle(
      currentThumbUrl,
      updateProduct ? t('edit.editProduct') : t('edit.newProductTitle'),
      t('common.completeData'),
      false,
      !currentThumbUrl ? PhotoCamera : null,
      handleAvatarClick
    );

    toggleFab(false);

    const fetchData = async () => {
      try {
        const productsForInput = await fetchForInput('products', 'products_for_input');
        setCategories(productsForInput.products_categories);
        setUnits(productsForInput.products_units);
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);

        // Add a delay to focus to ensure component is fully rendered
        setTimeout(() => {
          if (refs.name.current) {
            refs.name.current.focus();
          }
        }, 100);
      }
    };
    fetchData();
   
  }, []);

  useEffect(() => {
    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || product.thumb_url || '') : '';

    updateTitle(
      currentThumbUrl,
      updateProduct ? t('edit.editProduct') : t('edit.newProductTitle'),
      t('common.completeData'),
      false,
      !currentThumbUrl ? PhotoCamera : null,
      handleAvatarClick
    );
  }, [capturedPhoto, product.thumb_url, photoDeleted]);

  const handlePhotoAndThumbUpload = async (originalFile) => {
    try {
      setIsUploadingImage(true);

      const result = await uploadPhotoAndThumbnail({
        originalFile,
        tableName: 'products',
        onError: (error) => {
          snackbar(t('common.errorUploadingImages') + error);
        }
      });

      setUploadedImages({
        original: result.original,
        avatar: result.thumbnail
      });
      
      setPhotoDeleted(false);
      
    } catch (error) {
      snackbar(t('common.errorUploadingImages') + (error.message || error));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCameraCapture = () => {
    handleMenuClose();
    setShowInlineCamera(true);
  };

  const handleGallerySelect = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemovePhoto = () => {
    handleMenuClose();
    setPhotoDeleted(true);
    setCapturedPhoto(null);
    setUploadedImages({ original: null, avatar: null });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const unique_id = uuidv4();
      const extension = file.name.split('.').pop();
      const newFileName = `${unique_id}.${extension}`;
      
      const renamedFile = new File([file], newFileName, { type: file.type });
      
      const processedPhoto = {
        file: renamedFile,
        url: URL.createObjectURL(file),
        name: newFileName,
        size: file.size,
        type: file.type,
        isImage: true,
        isVideo: false,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      };
      
      setCapturedPhoto(processedPhoto);
      setPhotoDeleted(false);
      await handlePhotoAndThumbUpload(renamedFile);
    }
    
    event.target.value = '';
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setCapturedPhoto({
      url: previewUrl,
      file: file,
      isImage: true
    });

    setPhotoDeleted(false);
    await handlePhotoAndThumbUpload(file);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProduct({ ...product, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !validateNonEmpty("name", product.name, setErrors, refs.name) ||
      !validateNonEmpty("categories", product.categories?.length ? product.categories[0].name : '', setErrors, refs.categories) ||
      !validateNonEmpty("unit", product.unit?.name, setErrors, refs.unit)
    ) {
      return;
    }

    save();
  };

  const save = async () => {
    setIsLoading(true);
    try {
      // Map string display values to enum values for price format fields
      const productToSave = {
        ...product,
        format_p1: mapPriceFormatToEnum(product.format_p1),
        format_p2: mapPriceFormatToEnum(product.format_p2),
        format_p3: mapPriceFormatToEnum(product.format_p3),
      };

      productToSave.resources = [];
      if (uploadedImages.original && uploadedImages.avatar) {
        productToSave.resources = [
          {
            file_name: uploadedImages.original.file_name,
            table_name: "products",
            type: "PUBLIC",
            unique_id: uploadedImages.original.unique_id
          },
          {
            file_name: uploadedImages.avatar.file_name,
            table_name: "products", 
            type: "PUBLIC",
            unique_id: uploadedImages.avatar.unique_id
          }
        ];
      }

      if (photoDeleted) {
        productToSave.photo_deleted = 1;
      }
      
      console.log(removeNullsAndEmptys(productToSave));
      let response;
      if (updateProduct) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}products`,
          removeNullsAndEmptys(productToSave),
          { params: { product: '', id: updateProduct.id }, withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}products`,
          removeNullsAndEmptys(productToSave),
          { params: { product: '' }, withCredentials: true }
        );
      }

      navigate(`/main/view-product`, { state: { id: response.data.data.id } });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  // Map display string to enum value
  const mapPriceFormatToEnum = (displayFormat) => {
    if (!displayFormat) return null;
    
    if (displayFormat === t('common.fixed')) return "FIXED";
    if (displayFormat === t('common.costMargin')) return "COST_MARGIN";
    if (displayFormat === t('common.costMarginTax')) return "COST_MARGIN_TAX";
    
    return displayFormat;
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = [
        'name', 'categories', 'unit', 'bar_code', 'qr_code', 'web_page', 
        'quantity', 'min_quantity', 'cost', 'tax',
        // Price 1 fields
        'format_p1', 'margin_p1', 'fixed_p1',
        // Price 2 fields
        'format_p2', 'margin_p2', 'fixed_p2',
        // Price 3 fields
        'format_p3', 'margin_p3', 'fixed_p3'
      ];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        // Last field - submit the form
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        // Move to the next field
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading || isUploadingImage}
    >
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCameraCapture}>
          <ListItemIcon>
            <PhotoCamera />
          </ListItemIcon>
          <ListItemText>{t('common.camera')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleGallerySelect}>
          <ListItemIcon>
            <PhotoLibrary />
          </ListItemIcon>
          <ListItemText>{t('common.gallery')}</ListItemText>
        </MenuItem>
        {(capturedPhoto || product.photo_url || product.thumb_url) && !photoDeleted && (
          <MenuItem onClick={handleRemovePhoto}>
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            <ListItemText>{t('common.remove')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileSelect}
      />

      <InlinePhotoCapture 
        open={showInlineCamera}
        onPhotoConfirm={handleInlinePhotoConfirm}
        onClose={() => setShowInlineCamera(false)}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.nameStar')}
        name="name"
        value={product.name}
        onChange={handleChange}
        inputRef={refs.name}
        error={!!errors.name}
        helperText={errors.name}
        onKeyDown={(e) => handleKeyDown(e, 'name')}
      />

      <Autocomplete
        fullWidth
        multiple
        options={categories}
        getOptionLabel={(option) => (option ? option.name : "")} 
        value={product.categories || []}
        onChange={(e, newValue) =>
          setProduct({
            ...product,
            categories: newValue,
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('edit.categoriesStar')}
            name="categories"
            margin="normal"
            inputRef={refs.categories}
            error={!!errors.categories}
            helperText={errors.categories}
            onKeyDown={(e) => handleKeyDown(e, 'categories')}
          />
        )}
      />

      <Autocomplete
        fullWidth
        options={units}
        getOptionLabel={(option) => (option ? option.name : "")} 
        value={product.unit}
        onChange={(e, newValue) =>
          setProduct({
            ...product,
            unit: newValue,
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('edit.measurementUnit')}
            name="unit"
            margin="normal"
            inputRef={refs.unit}
            error={!!errors.unit}
            helperText={errors.unit}
            onKeyDown={(e) => handleKeyDown(e, 'unit')}
          />
        )}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.barCode')}
        name="bar_code"
        value={product.bar_code}
        onChange={handleChange}
        inputRef={refs.bar_code}
        error={!!errors.bar_code}
        helperText={errors.bar_code}
        onKeyDown={(e) => handleKeyDown(e, 'bar_code')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.qrCode')}
        name="qr_code"
        value={product.qr_code}
        onChange={handleChange}
        inputRef={refs.qr_code}
        error={!!errors.qr_code}
        helperText={errors.qr_code}
        onKeyDown={(e) => handleKeyDown(e, 'qr_code')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.webPage')}
        name="web_page"
        value={product.web_page}
        onChange={handleChange}
        inputRef={refs.web_page}
        error={!!errors.web_page}
        helperText={errors.web_page}
        onKeyDown={(e) => handleKeyDown(e, 'web_page')}
      />

      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setChecked(isChecked);
                setProduct({
                  ...product,
                  expires: isChecked ? 1 : 0
                });
              }}
            />
          }
          label={t('edit.expires')}
        />
      </Box>

      <TextField
        fullWidth
        margin="normal"
        label={t('common.currentQuantity')}
        name="quantity"
        value={product.quantity}
        onChange={handleChange}
        inputRef={refs.quantity}
        error={!!errors.quantity}
        helperText={t('edit.quantityResetHelper')}
        onKeyDown={(e) => handleKeyDown(e, 'quantity')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.minQuantity')}
        name="min_quantity"
        value={product.min_quantity}
        onChange={handleChange}
        inputRef={refs.min_quantity}
        error={!!errors.min_quantity}
        helperText={errors.min_quantity}
        onKeyDown={(e) => handleKeyDown(e, 'min_quantity')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.cost')}
        name="cost"
        value={product.cost}
        onChange={handleChange}
        inputRef={refs.cost}
        error={!!errors.cost}
        helperText={errors.cost}
        onKeyDown={(e) => handleKeyDown(e, 'cost')}
      />

      <TextField
        fullWidth
        margin="normal"
        label={t('common.tax')}
        name="tax"
        value={product.tax}
        onChange={handleChange}
        inputRef={refs.tax}
        error={!!errors.tax}
        helperText={errors.tax}
        onKeyDown={(e) => handleKeyDown(e, 'tax')}
      />

      {/* Price 1 Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: "gray", fontWeight: "bold", mb: 1 }}>
          {t('edit.price1')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={[t('common.fixed'), t('common.costMargin'), t('common.costMarginTax')]}
            getOptionLabel={(option) => option}
            value={product.format_p1}
            onChange={(e, newValue) => {
              setProduct(prev => ({
                ...prev,
                format_p1: newValue
              }));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('common.calendarTypeLabel')} 
                name="format_p1"
                inputRef={refs.format_p1}
                onKeyDown={(e) => handleKeyDown(e, 'format_p1')}
              />
            )}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.marginPercent')}
            name="margin_p1"
            value={product.margin_p1}
            onChange={handleChange}
            inputRef={refs.margin_p1}
            onKeyDown={(e) => handleKeyDown(e, 'margin_p1')}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.price')}
            name="fixed_p1"
            value={product.fixed_p1}
            onChange={handleChange}
            inputRef={refs.fixed_p1}
            onKeyDown={(e) => handleKeyDown(e, 'fixed_p1')}
            disabled={product.format_p1 !== t('common.fixed')}
          />
        </Box>
      </Box>

      {/* Price 2 Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: "gray", fontWeight: "bold", mb: 1 }}>
          {t('edit.price2')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={[t('common.fixed'), t('common.costMargin'), t('common.costMarginTax')]}
            getOptionLabel={(option) => option}
            value={product.format_p2}
            onChange={(e, newValue) => {
              setProduct(prev => ({
                ...prev,
                format_p2: newValue
              }));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('common.calendarTypeLabel')} 
                name="format_p2"
                inputRef={refs.format_p2}
                onKeyDown={(e) => handleKeyDown(e, 'format_p2')}
              />
            )}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.marginPercent')}
            name="margin_p2"
            value={product.margin_p2}
            onChange={handleChange}
            inputRef={refs.margin_p2}
            onKeyDown={(e) => handleKeyDown(e, 'margin_p2')}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.price')}
            name="fixed_p2"
            value={product.fixed_p2}
            onChange={handleChange}
            inputRef={refs.fixed_p2}
            onKeyDown={(e) => handleKeyDown(e, 'fixed_p2')}
            disabled={product.format_p2 !== t('common.fixed')}
          />
        </Box>
      </Box>

      {/* Price 3 Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle1" sx={{ color: "gray", fontWeight: "bold", mb: 1 }}>
          {t('edit.price3')}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={[t('common.fixed'), t('common.costMargin'), t('common.costMarginTax')]}
            getOptionLabel={(option) => option}
            value={product.format_p3}
            onChange={(e, newValue) => {
              setProduct(prev => ({
                ...prev,
                format_p3: newValue
              }));
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={t('common.calendarTypeLabel')} 
                name="format_p3"
                inputRef={refs.format_p3}
                onKeyDown={(e) => handleKeyDown(e, 'format_p3')}
              />
            )}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.marginPercent')}
            name="margin_p3"
            value={product.margin_p3}
            onChange={handleChange}
            inputRef={refs.margin_p3}
            onKeyDown={(e) => handleKeyDown(e, 'margin_p3')}
          />
          <TextField
            sx={{ flex: 1 }}
            label={t('common.price')}
            name="fixed_p3"
            value={product.fixed_p3}
            onChange={handleChange}
            inputRef={refs.fixed_p3}
            onKeyDown={(e) => handleKeyDown(e, 'fixed_p3')}
            disabled={product.format_p3 !== t('common.fixed')}
          />
        </Box>
      </Box>
    </EditPage>
  );
};

export default EditProduct;
